/**
 * Slice 15 — Cleaner Inventory step (Count −/+ · Level chips · Next).
 * Slice 21b — Package ready banner + Update count highlight after pickup.
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInventoryForCleaner, useSubmitInventory } from "@/hooks/useCleaner";
import { getInStockCount } from "@/lib/inventoryQuantity";
import { LEVEL_NOW_OPTIONS } from "@/lib/inventoryCatalog";
import { simpleFlowSegmentIndex } from "@/lib/turnoverWizardPlan";
import { SimpleStepHeader } from "@/components/cleaner/SimpleStepHeader";
import { CleanerMediaLightbox } from "@/components/cleaner/CleanerMediaLightbox";
import { InventoryReorderPickupBanner } from "@/components/cleaner/InventoryReorderPickupBanner";
import { usePendingPickupReorders } from "@/hooks/useInventoryReorders";
import { usePhotoGroups } from "@/hooks/usePhotoGroups";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { useRoomCoverData } from "@/hooks/useRoomCoverPhotos";
import { sortRoomsWalkOrder } from "@/lib/roomCoverPhotos";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";

function inventorySaveErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message.trim()) return err.message;
  if (err && typeof err === "object" && "message" in err) {
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  return fallback;
}

const LEVEL_LABEL_KEYS: Record<number, string> = {
  1: "levelFull",
  0.75: "levelThreeQuarters",
  0.5: "levelHalf",
  0.25: "levelQuarter",
  0: "levelEmpty",
};

type InvItem = {
  id: string;
  item_name: string;
  quantity_type?: string | null;
  current_count?: number | null;
  in_stock_qty?: number | null;
  current_value?: number | null;
  threshold_count?: number | null;
  threshold_value?: number | null;
  photo_url?: string | null;
  inventory_scope?: string | null;
  photo_group_id?: string | null;
};

interface Props {
  propertyId: string;
  cleaningTaskId: string;
  cleanerId?: string | null;
  readOnly?: boolean;
  onComplete: () => void;
  submitInventoryAsOwnerOrTeam?: {
    mutateAsync: (params: {
      propertyId: string;
      items: { item_id: string; item_name: string; count: number; fullness_value?: number }[];
      cleaningTaskId?: string | null;
      inventorySubmitPhase?: "guest" | "final";
      onlyUpdateItems?: boolean;
    }) => Promise<unknown>;
    isPending: boolean;
  };
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
  /** Slice 44 — quiet recall under Inv helper, e.g. "You left out: 2 TP · 2 paper towels". */
  leftOutRecall?: string | null;
}

function isLevel(item: InvItem): boolean {
  return item.quantity_type === "fullness";
}

function isLow(item: InvItem, count: number, level: number): boolean {
  if (isLevel(item)) {
    const t = item.threshold_value;
    if (t == null) return false;
    return level <= t;
  }
  const t = item.threshold_count;
  if (t == null) return false;
  return count <= t;
}

function nearestLevel(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value)) return 1;
  let best: number = LEVEL_NOW_OPTIONS[0].value;
  let bestDist = Math.abs(best - value);
  for (const o of LEVEL_NOW_OPTIONS) {
    const d = Math.abs(o.value - value);
    if (d < bestDist) {
      best = o.value;
      bestDist = d;
    }
  }
  return best;
}

export function SimpleInventoryStep({
  propertyId,
  cleaningTaskId,
  cleanerId = null,
  readOnly = false,
  onComplete,
  submitInventoryAsOwnerOrTeam,
  onBack,
  onSelectSegment,
  includeMaintenance = false,
  includeQuickCheck = false,
  includeGuestSetup = false,
  leftOutRecall = null,
}: Props) {
  const { t, tDynamic } = useTranslation();
  const { data: rawItems = [], isLoading } = useInventoryForCleaner(propertyId);
  const { data: pendingReorders = [] } = usePendingPickupReorders(propertyId);
  const { data: photoGroups = [] } = usePhotoGroups(propertyId);
  const { data: propertyRooms = [] } = usePropertyRooms(propertyId);
  const { data: coverData = {} } = useRoomCoverData(propertyId);
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const submitDefault = useSubmitInventory();
  const submitInventory = submitInventoryAsOwnerOrTeam ?? submitDefault;

  const items = useMemo(
    () => (rawItems as InvItem[]).filter((i) => i.inventory_scope !== "unit"),
    [rawItems],
  );

  /** Items sit under the spot they live in (photo + name); the rest are "In the open". */
  const sections = useMemo(() => {
    const roomOrder = new Map(
      sortRoomsWalkOrder(
        propertyRooms.filter((r) => !r.archived_at),
        coverData,
      ).map((r, i) => [r.id, i] as const),
    );
    const groupsSorted = [...photoGroups].sort(
      (a, b) =>
        (roomOrder.get(a.room_id) ?? 99) - (roomOrder.get(b.room_id) ?? 99) || a.sort_order - b.sort_order,
    );
    const known = new Set(groupsSorted.map((g) => g.id));
    const out: { key: string; name: string | null; photoUrl: string | null; items: InvItem[] }[] = [];
    for (const g of groupsSorted) {
      const list = items.filter((i) => i.photo_group_id === g.id);
      if (list.length > 0) out.push({ key: g.id, name: g.name, photoUrl: g.reference_photo_url, items: list });
    }
    const loose = items.filter((i) => !i.photo_group_id || !known.has(i.photo_group_id));
    if (loose.length > 0) out.push({ key: "open", name: null, photoUrl: null, items: loose });
    return out;
  }, [items, photoGroups, propertyRooms, coverData]);

  const [counts, setCounts] = useState<Record<string, number>>({});
  const [levels, setLevels] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number } | null>(null);
  const [highlightIds, setHighlightIds] = useState<Set<string>>(() => new Set());
  const [touchedIds, setTouchedIds] = useState<Set<string>>(() => new Set());
  const [justPicked, setJustPicked] = useState(false);
  const [nudgeNames, setNudgeNames] = useState<string[]>([]);

  const photoUrls = useMemo(
    () => items.map((i) => i.photo_url).filter((u): u is string => Boolean(u?.trim())),
    [items],
  );

  useEffect(() => {
    const nextCounts: Record<string, number> = {};
    const nextLevels: Record<string, number> = {};
    for (const item of items) {
      if (isLevel(item)) {
        nextLevels[item.id] = nearestLevel(item.current_value);
      } else {
        nextCounts[item.id] = getInStockCount(item);
      }
    }
    setCounts(nextCounts);
    setLevels(nextLevels);
  }, [items]);

  const markTouched = (id: string) => {
    setTouchedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const persistOne = async (item: InvItem, patch: { count?: number; fullness?: number }) => {
    if (readOnly) return;
    try {
      await submitInventory.mutateAsync({
        propertyId,
        cleaningTaskId,
        onlyUpdateItems: true,
        items: [
          {
            item_id: item.id,
            item_name: item.item_name,
            count: patch.count ?? getInStockCount(item),
            fullness_value: patch.fullness,
          },
        ],
      });
    } catch {
      /* keep local; Next will retry full save */
    }
  };

  const bumpCount = (item: InvItem, delta: number) => {
    if (readOnly) return;
    markTouched(item.id);
    const cur = counts[item.id] ?? getInStockCount(item);
    const next = Math.max(0, cur + delta);
    setCounts((prev) => ({ ...prev, [item.id]: next }));
    void persistOne(item, { count: next });
  };

  const setLevel = (item: InvItem, value: number) => {
    if (readOnly) return;
    markTouched(item.id);
    setLevels((prev) => ({ ...prev, [item.id]: value }));
    void persistOne(item, { fullness: value, count: 0 });
  };

  const handleNext = async () => {
    if (readOnly) {
      onComplete();
      return;
    }
    setBusy(true);
    try {
      if (items.length > 0) {
        const rows = items.map((item) => {
          if (isLevel(item)) {
            return {
              item_id: item.id,
              item_name: item.item_name,
              count: 0,
              fullness_value: levels[item.id] ?? nearestLevel(item.current_value),
            };
          }
          return {
            item_id: item.id,
            item_name: item.item_name,
            count: counts[item.id] ?? getInStockCount(item),
          };
        });
        await submitInventory.mutateAsync({
          propertyId,
          cleaningTaskId,
          items: rows,
          inventorySubmitPhase: "final",
        });
      } else {
        await submitInventory.mutateAsync({
          propertyId,
          cleaningTaskId,
          items: [],
          inventorySubmitPhase: "final",
        });
      }
      onComplete();
    } catch (err) {
      toast({
        title: t("couldNotSave"),
        description: inventorySaveErrorMessage(err, t("tryAgain")),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const pendingNudgeNames = nudgeNames.filter((n) => {
    const match = items.find((i) => i.item_name === n);
    if (!match) return true;
    return !touchedIds.has(match.id);
  });
  const showNudge = justPicked && pendingNudgeNames.length > 0 && pendingReorders.length === 0;

  return (
    <div className={cn(CLEANER_SIMPLE_STEP_SHELL, "relative text-[#0f1720]")}>
      <SimpleStepHeader
        title={t("inventoryTitle")}
        activeSegment={simpleFlowSegmentIndex("inventory_single", { includeMaintenance, includeQuickCheck, includeGuestSetup })}
        includeMaintenance={includeMaintenance}
        includeQuickCheck={includeQuickCheck}
        includeGuestSetup={includeGuestSetup}
        onBack={onBack}
        onSelectSegment={onSelectSegment}
        subtitle={
          <div className="flex flex-col gap-0.5 pt-0.5">
            <div className="text-[15px] font-bold text-[#0f1720]">{t("closetAfterLeaveOut")}</div>
            {leftOutRecall ? (
              <div className="text-[13px] font-medium text-[#8494a2]">{tDynamic(leftOutRecall)}</div>
            ) : null}
          </div>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3">
        {cleanerId && (pendingReorders.length > 0 || justPicked) ? (
          <div className="mb-3.5 flex flex-col gap-2.5">
            <InventoryReorderPickupBanner
              reorders={pendingReorders}
              cleanerId={cleanerId}
              propertyId={propertyId}
              justPicked={justPicked && pendingReorders.length === 0}
              onPickedUp={({ itemIds, names }) => {
                setJustPicked(true);
                const byName = items
                  .filter((i) => names.some((n) => n.toLowerCase() === i.item_name.toLowerCase()))
                  .map((i) => i.id);
                setHighlightIds(new Set([...itemIds, ...byName]));
                setTouchedIds(new Set());
                setNudgeNames(names);
              }}
            />
            {showNudge ? (
              <div className="rounded-[14px] bg-[#f4fbfa] px-4 py-3 text-[14px] font-semibold text-[#17796e]">
                {t("updateCount")}
                <span className="font-medium text-[#64748b]"> · {pendingNudgeNames.join(" · ")}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        {isLoading ? (
          <div className="flex justify-center py-16 text-[#64748b]">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <div className="text-[22px] font-bold text-[#0f1720]">{t("noSuppliesToCount")}</div>
            <div className="text-[15px] font-medium text-[#64748b]">{t("tapNextToContinue")}</div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {sections.map((sec) => {
              const compact = sec.items.length >= 4;
              return (
                <div key={sec.key} className="flex flex-col gap-2">
                  {sections.length > 1 || sec.name ? (
                    <div className="flex items-center gap-2.5 px-1">
                      {sec.photoUrl ? (
                        <button
                          type="button"
                          onClick={() => setLightbox({ urls: [sec.photoUrl!], index: 0 })}
                          className="h-9 w-9 shrink-0 overflow-hidden rounded-[9px] bg-[#eff4f7]"
                          aria-label={sec.name ?? ""}
                        >
                          <img src={sec.photoUrl} alt="" className="h-full w-full object-cover" />
                        </button>
                      ) : (
                        <span className="h-9 w-9 shrink-0 rounded-[9px] bg-[repeating-linear-gradient(135deg,#e6edf1_0_6px,#eff4f7_6px_12px)]" aria-hidden />
                      )}
                      <div className="min-w-0 flex-1 truncate text-[15.5px] font-bold text-[#0f1720]">
                        {sec.name ? tDynamic(sec.name) : t("bpOutInOpen")}
                      </div>
                      <div className="text-[12.5px] font-bold text-[#8494a2]">
                        {sec.items.length === 1 ? t("invOneItem") : t("invNItems").replace("{{n}}", String(sec.items.length))}
                      </div>
                    </div>
                  ) : null}
                  <div className="overflow-hidden rounded-[18px] bg-white">
                    {sec.items.map((item, idx) => {
                      const level = isLevel(item);
                      const count = counts[item.id] ?? getInStockCount(item);
                      const levelVal = levels[item.id] ?? nearestLevel(item.current_value);
                      const low = isLow(item, count, levelVal);
                      const nudge = justPicked && highlightIds.has(item.id) && !touchedIds.has(item.id);
                      const expanded = !compact || openRowId === item.id;
                      const valueText = level
                        ? LEVEL_LABEL_KEYS[levelVal]
                          ? t(LEVEL_LABEL_KEYS[levelVal])
                          : String(levelVal)
                        : String(count);
                      const btn = cn(
                        "flex h-12 w-12 items-center justify-center rounded-[14px] text-2xl font-bold text-[#475569]",
                        nudge ? "bg-[#e4f5f2]" : "bg-[#f4f7f9]",
                        readOnly && "opacity-60",
                      );
                      return (
                        <div
                          key={item.id}
                          className={cn(
                            "px-3.5 py-3",
                            idx > 0 && "border-t border-[#eef2f5]",
                            nudge && "bg-[#f4fbfa]",
                            low && !nudge && "bg-[#fdf8f2]",
                          )}
                        >
                          <div className="flex items-center gap-3">
                            {item.photo_url ? (
                              <button
                                type="button"
                                onClick={() => {
                                  const idx2 = photoUrls.indexOf(item.photo_url!);
                                  setLightbox({ urls: photoUrls.length > 0 ? photoUrls : [item.photo_url!], index: idx2 >= 0 ? idx2 : 0 });
                                }}
                                className="h-10 w-10 shrink-0 overflow-hidden rounded-[10px] bg-[#eff4f7]"
                                aria-label={`View ${item.item_name}`}
                              >
                                <img src={item.photo_url} alt="" loading="lazy" className="h-full w-full object-cover" />
                              </button>
                            ) : null}
                            <button
                              type="button"
                              disabled={!compact}
                              onClick={() => setOpenRowId((cur) => (cur === item.id ? null : item.id))}
                              className="flex min-w-0 flex-1 items-center gap-2 text-left"
                            >
                              <span className="truncate text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">{tDynamic(item.item_name)}</span>
                              {nudge ? (
                                <span className="rounded-full bg-[#e4f5f2] px-2 py-0.5 text-[11px] font-bold text-[#17796e]">{t("update")}</span>
                              ) : low ? (
                                <span className="rounded-full bg-[#fdf3e5] px-2 py-0.5 text-[11px] font-bold text-[#b45309]">{t("low")}</span>
                              ) : null}
                            </button>
                            {compact ? (
                              <button
                                type="button"
                                onClick={() => setOpenRowId((cur) => (cur === item.id ? null : item.id))}
                                className="flex shrink-0 items-center gap-1 text-[16px] font-bold tabular-nums text-[#0f1720]"
                                aria-label={item.item_name}
                              >
                                {valueText}
                                <ChevronDown className={cn("h-4 w-4 text-[#8494a2] transition-transform", expanded && "rotate-180")} />
                              </button>
                            ) : !level ? (
                              <div className="flex shrink-0 items-center gap-2">
                                <button type="button" disabled={readOnly} onClick={() => bumpCount(item, -1)} className={btn}>−</button>
                                <div className="min-w-[40px] text-center text-[26px] font-bold tabular-nums tracking-[-0.5px]">{count}</div>
                                <button type="button" disabled={readOnly} onClick={() => bumpCount(item, 1)} className={cn(btn, "text-[#15776d]")}>+</button>
                              </div>
                            ) : null}
                          </div>
                          {level && expanded ? (
                            <div className="mt-2.5 flex gap-1.5">
                              {LEVEL_NOW_OPTIONS.map((o) => {
                                const on = levelVal === o.value;
                                return (
                                  <button
                                    key={o.value}
                                    type="button"
                                    disabled={readOnly}
                                    onClick={() => setLevel(item, o.value)}
                                    className={cn(
                                      "flex h-[46px] flex-1 items-center justify-center rounded-[13px] text-[15px] font-bold",
                                      on ? "bg-[#15776d] text-white" : "bg-[#f4f7f9] text-[#475569]",
                                      readOnly && "opacity-60",
                                    )}
                                  >
                                    {LEVEL_LABEL_KEYS[o.value] ? t(LEVEL_LABEL_KEYS[o.value]) : o.label}
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                          {compact && !level && expanded ? (
                            <div className="mt-2.5 flex items-center justify-end gap-2.5">
                              <button type="button" disabled={readOnly} onClick={() => bumpCount(item, -1)} className={btn}>−</button>
                              <div className="min-w-[48px] text-center text-[26px] font-bold tabular-nums">{count}</div>
                              <button type="button" disabled={readOnly} onClick={() => bumpCount(item, 1)} className={cn(btn, "text-[#15776d]")}>+</button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={CLEANER_SIMPLE_CTA_FOOTER}>
        <button
          type="button"
          disabled={busy || submitInventory.isPending}
          onClick={() => void handleNext()}
          className="flex h-[58px] w-full items-center justify-center rounded-[18px] bg-[#24a89a] text-[18px] font-bold text-white hover:bg-[#1b8378] disabled:opacity-60"
        >
          {busy || submitInventory.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            t("next")
          )}
        </button>
      </div>

      {lightbox ? (
        <CleanerMediaLightbox
          urls={lightbox.urls}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndexChange={(i) => setLightbox((prev) => (prev ? { ...prev, index: i } : null))}
        />
      ) : null}
    </div>
  );
}
